import { TPSScores } from '../types/tps.types';
import type { SLOANDetail } from '../types/tps.types';
import { calculateBigFiveEnhanced } from './bigfive.enhanced';

// SLOAN (HEXACO Letter Code) Mapping
// S/R: Social vs Reserved (Extraversion)
// L/C: Limbic vs Calm (Neuroticism/Emotionality - inverted)
// O/U: Organized vs Unstructured (Conscientiousness)
// A/E: Accommodating vs Egocentric (Agreeableness)
// I/N: Inquisitive vs Non-curious (Openness to Experience)

const DIMENSION_THRESHOLD = 50; // Scores >= 50 are "high", < 50 are "low"

export function calculateSLOANType(scores: TPSScores): SLOANDetail {
  // First calculate HEXACO scores
  const hexacoDetail = calculateBigFiveEnhanced(scores);
  
  // Extract dimension scores (normalized to 0-100 scale)
  const extraversion = normalizeScore(hexacoDetail.dimensions['Extraversion'].score);
  const emotionality = normalizeScore(hexacoDetail.dimensions['Emotionality'].score);
  const conscientiousness = normalizeScore(hexacoDetail.dimensions['Conscientiousness'].score);
  const agreeableness = normalizeScore(hexacoDetail.dimensions['Agreeableness'].score);
  const openness = normalizeScore(hexacoDetail.dimensions['Openness to Experience'].score);
  
  // Calculate strength (distance from threshold) for confidence
  const calculateStrength = (score: number): number => {
    return Math.abs(score - DIMENSION_THRESHOLD);
  };
  
  // Determine letters and strengths
  const social_reserved = {
    letter: (extraversion >= DIMENSION_THRESHOLD ? 'S' : 'R') as 'S' | 'R',
    score: extraversion,
    strength: calculateStrength(extraversion)
  };
  
  const limbic_calm = {
    letter: (emotionality >= DIMENSION_THRESHOLD ? 'L' : 'C') as 'L' | 'C',
    score: emotionality,
    strength: calculateStrength(emotionality)
  };
  
  const organized_unstructured = {
    letter: (conscientiousness >= DIMENSION_THRESHOLD ? 'O' : 'U') as 'O' | 'U',
    score: conscientiousness,
    strength: calculateStrength(conscientiousness)
  };
  
  const accommodating_egocentric = {
    letter: (agreeableness >= DIMENSION_THRESHOLD ? 'A' : 'E') as 'A' | 'E',
    score: agreeableness,
    strength: calculateStrength(agreeableness)
  };
  
  const inquisitive_noncurious = {
    letter: (openness >= DIMENSION_THRESHOLD ? 'I' : 'N') as 'I' | 'N',
    score: openness,
    strength: calculateStrength(openness)
  };
  
  // Build SLOAN type code
  const type = `${social_reserved.letter}${limbic_calm.letter}${organized_unstructured.letter}${accommodating_egocentric.letter}${inquisitive_noncurious.letter}`;
  
  // Calculate overall confidence based on how clear-cut each dimension is
  const avgStrength = (
    social_reserved.strength +
    limbic_calm.strength +
    organized_unstructured.strength +
    accommodating_egocentric.strength +
    inquisitive_noncurious.strength
  ) / 5;
  
  // Convert strength to confidence percentage (0-50 strength -> 50-100 confidence)
  const confidence = 50 + avgStrength;
  
  // Preserve facet data from HEXACO
  const facets: Record<string, number> = {};
  Object.entries(hexacoDetail.dimensions).forEach(([dimName, dimData]: [string, any]) => {
    Object.entries(dimData.facets).forEach(([facetName, facetScore]: [string, any]) => {
      facets[`${dimName.toLowerCase().replace(/\s+/g, '_')}_${facetName}`] = normalizeScore(facetScore);
    });
  });
  
  return {
    type,
    dimensions: {
      social_reserved,
      limbic_calm,
      organized_unstructured,
      accommodating_egocentric,
      inquisitive_noncurious
    },
    confidence: Math.min(100, confidence),
    facets
  };
}

// Normalize scores from 0-10 range to 0-100 range
function normalizeScore(score: number): number {
  return Math.min(100, Math.max(0, score * 10));
}

// Get full dimension names for display
export function getSLOANDimensionName(letter: string, dimension: string): string {
  const names: Record<string, Record<string, string>> = {
    social_reserved: { S: 'Social', R: 'Reserved' }, // TPS-EXEMPT: Social=processing.reality.social
    limbic_calm: { L: 'Limbic', C: 'Calm' },
    organized_unstructured: { O: 'Organized', U: 'Unstructured' },
    accommodating_egocentric: { A: 'Accommodating', E: 'Egocentric' },
    inquisitive_noncurious: { I: 'Inquisitive', N: 'Non-curious' }
  };
  
  return names[dimension]?.[letter] || letter;
}

// Parse SLOAN type string into components
export function parseSLOANType(type: string): {
  social: 'S' | 'R';
  limbic: 'L' | 'C';
  organized: 'O' | 'U';
  accommodating: 'A' | 'E';
  inquisitive: 'I' | 'N';
} | null {
  if (!type || type.length !== 5) return null;
  
  const [s, l, o, a, i] = type.toUpperCase().split('');
  
  if (
    !['S', 'R'].includes(s) ||
    !['L', 'C'].includes(l) ||
    !['O', 'U'].includes(o) ||
    !['A', 'E'].includes(a) ||
    !['I', 'N'].includes(i)
  ) {
    return null;
  }
  
  return {
    social: s as 'S' | 'R',
    limbic: l as 'L' | 'C',
    organized: o as 'O' | 'U',
    accommodating: a as 'A' | 'E',
    inquisitive: i as 'I' | 'N'
  };
}

// Validate SLOAN type string
export function isValidSLOANType(type: string): boolean {
  return parseSLOANType(type) !== null;
}

// Get human-readable description of SLOAN type
export function getSLOANTypeDescription(type: string): string {
  const parsed = parseSLOANType(type);
  if (!parsed) return type;
  
  const parts = [
    getSLOANDimensionName(parsed.social, 'social_reserved'),
    getSLOANDimensionName(parsed.limbic, 'limbic_calm'),
    getSLOANDimensionName(parsed.organized, 'organized_unstructured'),
    getSLOANDimensionName(parsed.accommodating, 'accommodating_egocentric'),
    getSLOANDimensionName(parsed.inquisitive, 'inquisitive_noncurious')
  ];
  
  return parts.join(', ');
}
